/* eslint-disable max-lines */
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { LiveViewSwitch } from '@hakimo-ui/hakimo/feature-shared';
import { Camera } from '@hakimo-ui/hakimo/types';
import { HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_ASPECT,
  computeAutoLayout,
  computeFixedLayout,
} from './auto-layout';
import MultiCamTile from './MultiCamTile';
import SortableTile from './SortableTile';
import {
  GRID_GAP_PX,
  GridSize,
  MAX_AUTO_CAMERAS,
  MAX_COLS,
  MAX_ROWS,
} from '../../util';

interface Props {
  cameras: Camera[];
  override: GridSize | null;
  isFetching: boolean;
  isActive?: boolean;
  onRemoveCamera: (cameraId: string) => void;
  onReorder?: (orderedIds: string[]) => void;
}

export function MultiCamGrid(props: Props) {
  const {
    cameras,
    override,
    isFetching,
    isActive = true,
    onRemoveCamera,
    onReorder,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [aspects, setAspects] = useState<Record<string, number>>({});
  const [zoomedIds, setZoomedIds] = useState<Set<string>>(new Set());
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const onZoomChange = useCallback((cameraId: string, isZoomed: boolean) => {
    setZoomedIds((prev) => {
      if (prev.has(cameraId) === isZoomed) return prev;
      const next = new Set(prev);
      if (isZoomed) next.add(cameraId);
      else next.delete(cameraId);
      return next;
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () =>
      setSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setAspects((prev) => {
      const ids = new Set(cameras.map((cam) => cam.id));
      const next: Record<string, number> = {};
      let changed = false;
      Object.keys(prev).forEach((id) => {
        if (ids.has(id)) next[id] = prev[id];
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [cameras]);

  const onAspectRatio = useCallback(
    (cameraId: string, ratio: number) =>
      setAspects((prev) =>
        Math.abs((prev[cameraId] ?? 0) - ratio) < 0.001
          ? prev
          : { ...prev, [cameraId]: ratio }
      ),
    []
  );

  const { boxes, totalHeight } = useMemo(() => {
    const aspectList = cameras.map((cam) => aspects[cam.id] ?? DEFAULT_ASPECT);
    if (override) {
      return computeFixedLayout(
        aspectList,
        size.width,
        size.height,
        GRID_GAP_PX,
        override.cols,
        override.rows
      );
    }
    if (aspectList.length > MAX_AUTO_CAMERAS) {
      return computeFixedLayout(
        aspectList,
        size.width,
        size.height,
        GRID_GAP_PX,
        MAX_COLS,
        MAX_ROWS
      );
    }
    return computeAutoLayout(aspectList, size.width, size.height, GRID_GAP_PX);
  }, [cameras, aspects, override, size.width, size.height]);

  const showZoomControls = override
    ? override.cols <= 3 && override.rows <= 3
    : cameras.length <= 6;

  const canReorder = !!onReorder && cameras.length > 1;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = cameras.map((cam) => cam.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder?.(arrayMove(ids, oldIndex, newIndex));
  };

  const renderTile = (cam: Camera) => (
    <MultiCamTile
      camera={cam}
      onRemove={() => onRemoveCamera(cam.id)}
      showRemove={cameras.length > 1}
      showZoomControls={showZoomControls}
      onAspectRatio={(ratio) => onAspectRatio(cam.id, ratio)}
      paused={!isActive}
      onZoomChange={(isZoomed) => onZoomChange(cam.id, isZoomed)}
    >
      <LiveViewSwitch
        cameraId={cam.id}
        livestreamUrl={cam.livestreamUrl}
        controlsList={['noForwardBackward', 'noPlayPause']}
      />
    </MultiCamTile>
  );

  let content: JSX.Element;
  if (isFetching && cameras.length === 0) {
    content = (
      <div className="flex h-full items-center justify-center">
        <HakimoSpinner />
      </div>
    );
  } else if (cameras.length === 0) {
    content = (
      <div className="dark:border-ondark-line-2 dark:text-ondark-text-2 text-onlight-text-3 flex h-full items-center justify-center border-2 border-dashed">
        No cameras to display
      </div>
    );
  } else {
    content = (
      <div
        className="relative w-full"
        style={{ height: Math.max(totalHeight, size.height) }}
      >
        <SortableContext
          items={cameras.map((cam) => cam.id)}
          strategy={rectSortingStrategy}
        >
          {cameras.map((cam, i) => {
            const box = boxes[i];
            if (!box) return null;
            return (
              <SortableTile
                key={cam.id}
                id={cam.id}
                box={box}
                disabled={!canReorder || zoomedIds.has(cam.id)}
              >
                {renderTile(cam)}
              </SortableTile>
            );
          })}
        </SortableContext>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <div
        ref={containerRef}
        className="h-full overflow-y-auto overflow-x-hidden"
      >
        {content}
      </div>
    </DndContext>
  );
}

export default MultiCamGrid;
